import React, { useState } from 'react'
import CloseIcon from 'transfix-icons/build/CloseIcon'
import { Button } from 'transfix-ui/components/Button'
import FormattedPhoneNumber from 'transfix-ui/components/FormattedPhoneNumber'
import { IconButton } from 'transfix-ui/components/IconButton'
import { Box, Card, Grid, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { IError } from 'types/FMSGraphqlTypes'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { FP_HOME } from 'apps/fleet-planner/config'
import { useActivationContext } from 'apps/fleet-planner/context'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { DeleteIcon } from 'transfix-icons'
import { postMessageToMobileApp } from 'utils/mobileApp'
import { ProvideInsuranceForm } from './ProvideInsuranceForm'
import {
  IProvideInsuranceContact,
  TProvideInsuranceSchema,
} from './ProvideInsuranceForm/ProvideInsuranceSchema'
import { parseData, parseArrayData } from './ProvideInsuranceForm/utils'
import { EActivationStep, EMobileMessage } from './constants'
import { useCreateMotorCarrierInsuranceContactsMutation } from './graphql/CreateMotorCarrierInsuranceContacts'

const useProvideInsuranceStyles = makeStyles({
  contactDetails: {
    backgroundColor: brandColors.coolGray1,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: '10px',
  },
})

export function ProvideInsurance() {
  const { trackEvent } = useFMSAnalytics()
  const isW9SkipEnabled = useFeatureFlag('fmsSkipW9')
  const [agentContacts, setAgentContacts] = useState<IProvideInsuranceContact[]>([])
  const [showFields, setShowFields] = useState<boolean>(true)
  const [errorMessages, setErrorMessages] = useState<Pick<IError, 'message'>[]>([])
  const {
    carrierDetails,
    hasW9,
    isSafetyLetterNeeded,
    mobileApp,
    setCurrentStep,
  } = useActivationContext()
  const { fleetPlannerMotorCarrierId = '' } = carrierDetails || {}
  const classes = useProvideInsuranceStyles()

  const [
    createMotorCarrierInsuranceContacts,
    { loading },
  ] = useCreateMotorCarrierInsuranceContactsMutation()
  const fileDeleteHandler = (id: IProvideInsuranceContact['id']) => {
    if (id) {
      setAgentContacts(agentContacts.filter(file => file.id !== id))
    }
  }

  const determineNextStep = (mobileMessage = EMobileMessage.DOCUMENT_UPLOAD_COMPLETE) => {
    // TODO: delete isW9SkipEnabled FF once no longer needed
    if (hasW9 && !isSafetyLetterNeeded && isW9SkipEnabled) {
      postMessageToMobileApp(mobileApp, mobileMessage)
      window.location.replace(`${__ROOT__}/fms${FP_HOME}`)
    } else if (hasW9 && isSafetyLetterNeeded && isW9SkipEnabled) {
      setCurrentStep(EActivationStep.safetyLetter)
    } else {
      setCurrentStep(EActivationStep.documents)
    }
  }

  const onErrorHandler = async () => {
    if (agentContacts.length > 0) {
      const formattedContacts = parseArrayData(agentContacts)

      const { data } = await createMotorCarrierInsuranceContacts({
        variables: {
          input: { insuranceContacts: formattedContacts },
        },
      })
      const { createMotorCarrierInsuranceContacts: carrierInsuranceContact } = data || {}
      const { insuranceContacts } = carrierInsuranceContact || {}
      if (insuranceContacts?.length) {
        determineNextStep()
      } else {
        trackEvent('Setup Guide', 'UPLOAD_INSURANCE_FAILURE')
      }
    }
  }

  const onSuccessHandler = async (contact: TProvideInsuranceSchema) => {
    try {
      const contactData = parseData(contact)
      const formattedContacts = parseArrayData(agentContacts)

      const isContactDataValid = Object.values(contactData).every(value => !!value)
      const contacts = isContactDataValid ? [...formattedContacts, contactData] : formattedContacts

      const { data } = await createMotorCarrierInsuranceContacts({
        variables: {
          input: { insuranceContacts: contacts },
        },
      })
      const { createMotorCarrierInsuranceContacts: carrierInsuranceContact } = data || {}
      const { insuranceContacts, errors = [] } = carrierInsuranceContact || {}
      if (insuranceContacts && insuranceContacts.length > 0) {
        determineNextStep()
      } else {
        trackEvent('Setup Guide', 'UPLOAD_INSURANCE_FAILURE', { error: errors })
        setErrorMessages(errors)
      }
    } catch (error) {
      trackEvent('Setup Guide', 'UPLOAD_INSURANCE_FAILURE', { error })
      setErrorMessages([
        { message: 'Something went wrong, please check your information and try submitting again' },
      ])
    }
  }

  const showFieldsHandler = () => {
    setShowFields(false)
  }

  const skipHandler = () => {
    determineNextStep(EMobileMessage.SKIPPED_DOCUMENT_UPLOAD)
  }

  return (
    <Box mb={2.5} mt={1.5}>
      <Box>
        <Grid container alignItems='center' justifyContent='center'>
          <Grid item xs={12} md={8} lg={6}>
            <Typography variant='h3'>Provide Insurance Information</Typography>
          </Grid>
        </Grid>
      </Box>
      <Box mt={2}>
        <Grid container alignItems='center' justifyContent='center'>
          <Grid item xs={12} md={8} lg={6}>
            <Typography variant='body1'>
              Please provide your insurance agent&apos;s contact details
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <Box mt={2}>
        <Grid container alignItems='center' justifyContent='center'>
          <Grid item xs={12} md={8} lg={6}>
            <Typography variant='body2'>
              Certificate(s) of Insurance Required to Haul:
              <ul>
                <li>Automobile/BiPD</li>
                <li>Cargo</li>
              </ul>
            </Typography>
          </Grid>
        </Grid>
      </Box>
      {agentContacts.map((contact: IProvideInsuranceContact) => {
        const { email, fullName, id, insuranceDocuments, phoneNumber } = contact || {}
        return (
          <>
            <Box mb={2} key={id}>
              <Grid container alignItems='center' justifyContent='center'>
                <Grid item xs={12} md={8} lg={6}>
                  <Card>
                    <Box p={2} display='flex' justifyContent='space-between'>
                      <Box display='flex' flexDirection='column' alignItems='flex-start'>
                        <Typography variant='body2'>{fullName}</Typography>
                        <Typography variant='body2'>{email}</Typography>
                        <Typography variant='body2'>
                          <FormattedPhoneNumber phoneNumber={phoneNumber} />
                        </Typography>
                        {/* TODO: https://transfix.atlassian.net/browse/CE-750 */}
                        {insuranceDocuments?.map(document => (
                          <Typography variant='body2' key={document.fileFileName}>
                            {document.fileFileName}
                          </Typography>
                        ))}
                      </Box>

                      <IconButton
                        Icon={DeleteIcon}
                        label='Delete File'
                        size='large'
                        onClick={() => fileDeleteHandler(id)}
                      />
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </Box>
            {showFields && (
              <Grid container alignItems='center' justifyContent='center'>
                <Grid item xs={12} md={8} lg={6}>
                  <Box className={classes.contactDetails} my={2}>
                    <Typography variant='subtitle1'>
                      Add Additional Insurance Contact Details
                    </Typography>
                    <IconButton
                      label='close'
                      onClick={showFieldsHandler}
                      Icon={CloseIcon}
                      size='large'
                    />
                  </Box>
                </Grid>
              </Grid>
            )}
          </>
        )
      })}
      <Box mt={2}>
        <Grid container alignItems='center' justifyContent='center'>
          <Grid item xs={12} md={8} lg={6}>
            {!showFields ? (
              <Box mt={3}>
                <Grid container justifyContent='flex-end'>
                  <Button
                    color='tertiary'
                    size='large'
                    data-test='skip-button'
                    onClick={skipHandler}>
                    Skip and Send Later
                  </Button>

                  <Button
                    disabled={loading}
                    onClick={onSuccessHandler}
                    color='primary'
                    size='large'
                    data-test='submit-button'>
                    {loading ? 'Loading...' : 'Submit and Continue'}
                  </Button>
                </Grid>
              </Box>
            ) : (
              <ProvideInsuranceForm
                motorCarrierId={fleetPlannerMotorCarrierId}
                onSkip={skipHandler}
                onSubmitSuccess={onSuccessHandler}
                onSubmitError={onErrorHandler}
                setAgentContacts={setAgentContacts}
                errorMessages={errorMessages}
              />
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}
